import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { View, StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';

import { useA11ySelector } from '@sg/a11y-helpers';
import { HorizontalScrollRail, useDebounceFn, useResponsive } from '@sg/garnish';
import type { Product } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';

import { BagUpsellProductOption } from '../BagUpsellProductOption';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The rail that contains the upsell product cards for the bag.
 */
export const BagUpsellRail = (props: BagUpsellRailProps) => {
  const {
    upsellIdsBeingAdded,
    upsellProducts,
    addUpsellToBag,
    isAddingUpsellToBag,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    withoutHeader,
    withoutBorder,
    isDisabled = false,
  } = props;

  const a11ySelector = useA11ySelector('bag.upsell-products.container');
  const { minWidth } = useResponsive();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  // ─── Stepper Interactions ──────────────────────────────────────────────────

  const updateOpenSteppers = useCallback(
    (productId: string, isStepperShown: boolean) => {
      if (isStepperShown) {
        startChangingLineItemQuantity(productId);
        return;
      }

      stopChangingLineItemQuantity(productId);
    },
    [startChangingLineItemQuantity, stopChangingLineItemQuantity],
  );

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const trackRailScroll = useCallback(() => {
    track('bag_upsell.rail.scrolled');
  }, [track]);

  const debouncedTrackRailScroll = useDebounceFn(trackRailScroll, 500);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    withoutBorder ? styles.borderlessContainer : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles} {...a11ySelector.props}>
      <HorizontalScrollRail
        withoutHeaderBorder
        heading={withoutHeader ? undefined : formatMessage(messages.header)}
        titleStyle={styles.title}
        showNavControls={minWidth.isSM}
        gap={theme.spacing['2']}
        outerOffset={-theme.spacing['4']}
        countPosition={minWidth.isSM ? 'left' : 'right'}
        onScroll={debouncedTrackRailScroll}
        scrollGradientBaseColor={theme.colors.WHITE}
        scrollGradientOverlayEnabled
      >
        {upsellProducts.map((product, index) => {
          const isActive = upsellIdsBeingAdded.includes(product.id);
          const areSteppersActive = upsellIdsBeingAdded.length > 0;

          return (
            <BagUpsellProductOption
              key={product.id}
              product={product}
              railPosition={index + 1}
              size={minWidth.isSM ? 'md' : 'sm'}
              addUpsellToBag={addUpsellToBag}
              isAddingToCart={isAddingUpsellToBag && isActive}
              isDisabled={isDisabled || (areSteppersActive && !isActive)}
              updateOpenSteppers={updateOpenSteppers}
            />
          );
        })}
      </HorizontalScrollRail>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  header: {
    defaultMessage: 'You might also like',
    description: 'Bag Upsell Rail | Container Header',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
    borderBottomColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderBottomWidth: theme.spacing['2'],
    marginHorizontal: -theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  title: {
    marginTop: theme.spacing['1'],
    fontWeight: 'bold',
    fontSize: 18,
    color: theme.colors.DARK_KALE,
  },
  borderlessContainer: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type BagUpsellRailProps = {
  upsellProducts: UpsellProduct[];
  upsellIdsBeingAdded: string[];
  addUpsellToBag: (payload: {
    quantity: number;
    product: Partial<Product> & { isPersonalized?: boolean };
    railPosition: number;
  }) => void;
  isAddingUpsellToBag: boolean;
  startChangingLineItemQuantity: (lineItemId: string) => void;
  stopChangingLineItemQuantity: (lineItemId: string) => void;
  withoutHeader?: boolean;
  withoutBorder?: boolean;
  isDisabled?: boolean;
};

type UpsellProduct = Readonly<{
  id: string;
  name: string;
  cost: number;
  imageUrl: string;
  outOfStock: boolean;
  menuId: number;
  isPersonalized?: boolean;
}>;
